import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  BuildingOfficeIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ChartBarIcon,
  UsersIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  ShieldCheckIcon,
  PhotoIcon,
  CloudArrowUpIcon
} from '@heroicons/react/24/outline'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import PhoneNumberInput from '@/components/ui/PhoneNumberInput'
import { institutionsApi } from '@/api/institutionsApi'
import { groupsApi } from '@/api/groupsApi'
import toast from 'react-hot-toast'

interface Institution {
  id: string
  name: string
  slug: string
  domain?: string
  address: string
  city: string
  state: string
  phone: string
  email: string
  logo?: string
  status: 'active' | 'inactive' | 'pending' | 'suspended'
  subscriptionPlan: 'basic' | 'premium' | 'enterprise'
  studentCount: number
  teacherCount: number
  adminCount: number
  lastActivity: string
  createdAt: string
  validationMethods?: {
    bio: boolean
    rfidCards: boolean
    wristBands: boolean
    keyChain: boolean
  }
  settings: {
    rfidEnabled: boolean
    transportEnabled: boolean
    attendanceEnabled: boolean
    mealsEnabled: boolean
    timezone: string
    currency: string
    features?: {
      rfid?: boolean
      transport?: boolean
      attendance?: boolean
      meals?: boolean
    }
  }
}

// Form schemas for validation
const institutionDetailsSchema = z.object({
  name: z.string().min(2, 'Institution name must be at least 2 characters'),
  address: z.string().min(5, 'Address must be at least 5 characters'),
  city: z.string().min(2, 'City is required'),
  state: z.string().min(2, 'State/County is required'),
  phone: z.string().min(1, 'Phone number is required'),
  countryCode: z.string().min(1, 'Country code is required'),
  email: z.string().email('Valid email address is required'),
  subscriptionPlan: z.enum(['basic', 'premium', 'enterprise']),
  validationMethods: z.object({
    bio: z.boolean(),
    rfidCards: z.boolean(),
    wristBands: z.boolean(),
    keyChain: z.boolean()
  }).refine(
    (methods) => Object.values(methods).some(method => method === true),
    {
      message: "At least one validation method must be selected"
    }
  ),
  timezone: z.string().min(1, 'Timezone is required'),
  currency: z.string().min(2, 'Currency is required')
})

const schoolAdminSchema = z.object({
  firstName: z.string().min(2, 'First name must be at least 2 characters'),
  lastName: z.string().min(2, 'Last name must be at least 2 characters'),
  email: z.string().email('Valid email address is required'),
  phone: z.string().min(1, 'Phone number is required'),
  countryCode: z.string().min(1, 'Country code is required'),
  roleId: z.string().min(1, 'School Admin role group is required')
})

type InstitutionDetailsData = z.infer<typeof institutionDetailsSchema>
type SchoolAdminData = z.infer<typeof schoolAdminSchema>

const InstitutionsPage: React.FC = () => {
  const [institutions, setInstitutions] = useState<Institution[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [planFilter, setPlanFilter] = useState<string>('all')
  const [typeFilter, setTypeFilter] = useState<string>('all')

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize] = useState(10)
  const [totalPages, setTotalPages] = useState(1)
  const [totalInstitutions, setTotalInstitutions] = useState(0)
  const [sortBy, setSortBy] = useState<string>('createdAt')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [showAddModal, setShowAddModal] = useState(false)
  const [selectedInstitution, setSelectedInstitution] = useState<Institution | null>(null)
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid')
  const [currentStep, setCurrentStep] = useState(1)
  const [institutionFormData, setInstitutionFormData] = useState<InstitutionDetailsData | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const [availableGroups, setAvailableGroups] = useState<Array<{id: string, name: string}>>([])
  const [loadingGroups, setLoadingGroups] = useState(false)
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    totalStudents: 0,
    pendingApprovals: 0
  })

  // Form hooks for both steps
  const institutionForm = useForm<InstitutionDetailsData>({
    resolver: zodResolver(institutionDetailsSchema),
    defaultValues: {
      name: '',
      address: '',
      city: '',
      state: '',
      phone: '',
      countryCode: 'KE',
      email: '',
      subscriptionPlan: 'basic',
      validationMethods: {
        bio: false,
        rfidCards: true,
        wristBands: false,
        keyChain: false
      },
      timezone: 'Africa/Nairobi',
      currency: 'KES'
    }
  })

  const adminForm = useForm<SchoolAdminData>({
    resolver: zodResolver(schoolAdminSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      countryCode: 'KE',
      roleId: ''
    }
  })

  useEffect(() => {
    fetchInstitutions()
    fetchInstitutionStats()
  }, [currentPage, searchQuery, statusFilter, typeFilter, sortBy, sortOrder])

  // Fetch institution statistics
  const fetchInstitutionStats = async () => {
    try {
      const apiStats = await institutionsApi.getInstitutionStats()
      setStats({
        total: apiStats.total,
        active: apiStats.active,
        totalStudents: apiStats.totalUsers, // Using total users as approximation
        pendingApprovals: apiStats.total - apiStats.active // Inactive institutions
      })
    } catch (error) {
      console.error('Failed to fetch institution stats:', error)
    }
  }

  // Fetch School Admin groups when Step 2 is reached
  const fetchGroups = async () => {
    setLoadingGroups(true)
    try {
      const groups = await groupsApi.getGroups()
      // Filter for School Admin groups (groups that contain "admin" in name - case insensitive)
      const schoolAdminGroups = groups.filter(group =>
        group.name.toLowerCase().includes('admin') ||
        group.name.toLowerCase().includes('school admin') ||
        group.description.toLowerCase().includes('school admin')
      )
      setAvailableGroups(schoolAdminGroups.map(g => ({ id: g.id, name: g.name })))

      if (schoolAdminGroups.length === 0) {
        toast.error('No School Admin groups found. Please create a School Admin group first.')
      }
    } catch (error) {
      toast.error('Failed to load School Admin groups')
      setAvailableGroups([])
    } finally {
      setLoadingGroups(false)
    }
  }

  const fetchInstitutions = async () => {
    setLoading(true)
    try {
      // Build API parameters
      const params: any = {
        page: currentPage,
        limit: pageSize,
        sortBy: sortBy,
        sortOrder: sortOrder
      };

      // Add search filter
      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }

      // Add status filter
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      // Add type filter
      if (typeFilter !== 'all') {
        params.type = typeFilter;
      }

      console.log('Fetching institutions with params:', params);

      // Get enhanced data from API
      const response = await institutionsApi.getInstitutions(params);
      const apiInstitutions = response.institutions;

      // Define API institution type to include features in settings
      type ApiInstitution = {
        id: string
        name: string
        slug: string
        createdAt: string
        isActive: boolean
        subscriptionPlan: 'basic' | 'premium' | 'enterprise'
        validationMethods?: {
          bio: boolean
          rfidCards: boolean
          wristBands: boolean
          keyChain: boolean
        }
        settings: {
          address: string
          city: string
          state: string
          phone: string
          email: string
          timezone: string
          currency: string
          features?: {
            rfid?: boolean
            transport?: boolean
            attendance?: boolean
            meals?: boolean
          }
        }
      }

      // Convert API response to frontend format
      const formattedInstitutions: Institution[] = (apiInstitutions as ApiInstitution[]).map(inst => ({
        id: inst.id,
        name: inst.name,
        slug: inst.slug,
        domain: `${inst.slug}.smarttagschool.com`,
        address: inst.settings.address,
        city: inst.settings.city,
        state: inst.settings.state,
        phone: inst.settings.phone,
        email: inst.settings.email,
        status: inst.isActive ? 'active' : 'inactive',
        subscriptionPlan: inst.subscriptionPlan,
        studentCount: 0, // TODO: Get from user stats
        teacherCount: 0, // TODO: Get from user stats
        adminCount: 1, // Default for now
        lastActivity: '2 minutes ago', // TODO: Calculate from real data
        createdAt: inst.createdAt.split('T')[0],
        validationMethods: inst.validationMethods || {
          bio: false,
          rfidCards: true,
          wristBands: false,
          keyChain: false
        },
        settings: {
          rfidEnabled: inst.settings.features?.rfid ?? true,
          transportEnabled: inst.settings.features?.transport ?? true,
          attendanceEnabled: inst.settings.features?.attendance ?? true,
          mealsEnabled: inst.settings.features?.meals ?? false,
          timezone: inst.settings.timezone,
          currency: inst.settings.currency
        }
      }))

      setInstitutions(formattedInstitutions)

      // Update pagination state
      if (response.pagination) {
        setTotalPages(response.pagination.totalPages);
        setTotalInstitutions(response.pagination.totalInstitutions);
      }
    } catch (error) {
      console.error('Failed to fetch institutions:', error)
      toast.error('Failed to load institutions')

    } finally {
      setLoading(false)
    }
  }

  // Handle logo upload
  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file')
        return
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size must be less than 5MB')
        return
      }

      setLogoFile(file)

      // Create preview
      const reader = new FileReader()
      reader.onload = (e) => {
        setLogoPreview(e.target?.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleRemoveLogo = () => {
    setLogoFile(null)
    setLogoPreview(null)
  }

  // Handle step navigation
  const handleNextStep = async () => {
    const isValid = await institutionForm.trigger()
    if (isValid) {
      setInstitutionFormData(institutionForm.getValues())
      setCurrentStep(2)
      // Fetch groups for role selection in Step 2
      fetchGroups()
    }
  }

  const handlePrevStep = () => {
    setCurrentStep(1)
  }

  const handleModalClose = () => {
    setShowAddModal(false)
    setCurrentStep(1)
    setInstitutionFormData(null)
    setLogoFile(null)
    setLogoPreview(null)
    institutionForm.reset()
    adminForm.reset()
  }

  // Handle final submission
  const handleSubmitInstitution = async (adminData: SchoolAdminData) => {
    if (!institutionFormData) return

    setIsSubmitting(true)

    try {
      // Prepare data for API call
      const requestData = {
        institution: institutionFormData,
        admin: {
          ...adminData,
          // If no phone provided, use empty string
          phone: adminData.phone || undefined
        }
      }

      console.log('Creating institution with data:', requestData)
      if (logoFile) {
        console.log('Logo file will be uploaded separately:', logoFile.name, logoFile.size)
        // TODO: Implement file upload logic
      }

      // Call real API
      const result = await institutionsApi.createInstitutionWithAdmin(requestData)

      toast.success(`${institutionFormData.name} created successfully!`)
      console.log('Institution created:', result.institution)
      console.log('Admin created with temporary password:', result.admin.temporaryPassword)

      handleModalClose()
      fetchInstitutions() // Refresh the list
      fetchInstitutionStats() // Refresh the stats

    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to create institution. Please try again.'
      toast.error(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const filteredInstitutions = institutions.filter(institution => {
    const matchesSearch = institution.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         institution.city.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         institution.email.toLowerCase().includes(searchQuery.toLowerCase())

    const matchesStatus = statusFilter === 'all' || institution.status === statusFilter
    const matchesPlan = planFilter === 'all' || institution.subscriptionPlan === planFilter

    return matchesSearch && matchesStatus && matchesPlan
  })

  const getStatusBadge = (status: Institution['status']) => {
    const styles = {
      active: 'badge-success',
      inactive: 'badge-gray',
      pending: 'badge-warning',
      suspended: 'badge-error'
    }

    const labels = {
      active: 'Active',
      inactive: 'Inactive',
      pending: 'Pending',
      suspended: 'Suspended'
    }

    return (
      <span className={`badge ${styles[status]}`}>
        {labels[status]}
      </span>
    )
  }

  const getPlanBadge = (plan: Institution['subscriptionPlan']) => {
    const styles = {
      basic: 'badge-gray',
      premium: 'badge-primary',
      enterprise: 'badge-warning'
    }

    return (
      <span className={`badge ${styles[plan]}`}>
        {plan.charAt(0).toUpperCase() + plan.slice(1)}
      </span>
    )
  }

  const handleViewInstitution = (institution: Institution) => {
    setSelectedInstitution(institution)
    toast.success(`Viewing ${institution.name}`)
  }

  const handleEditInstitution = (institution: Institution) => {
    toast.success(`Edit ${institution.name} - Coming soon!`)
  }

  const handleDeleteInstitution = (institution: Institution) => {
    if (confirm(`Are you sure you want to delete ${institution.name}?`)) {
      toast.success(`${institution.name} deleted successfully`)
    }
  }

  const InstitutionCard = ({ institution }: { institution: Institution }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="dashboard-card hover:shadow-large transition-all duration-200"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-indigo-600 rounded-xl flex items-center justify-center">
            <BuildingOfficeIcon className="h-6 w-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{institution.name}</h3>
            <p className="text-sm text-gray-500">{institution.city}, {institution.state}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {getStatusBadge(institution.status)}
          {getPlanBadge(institution.subscriptionPlan)}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="text-center">
          <div className="text-xl font-bold text-gray-900">{institution.studentCount.toLocaleString()}</div>
          <div className="text-xs text-gray-500">Students</div>
        </div>
        <div className="text-center">
          <div className="text-xl font-bold text-gray-900">{institution.teacherCount}</div>
          <div className="text-xs text-gray-500">Teachers</div>
        </div>
        <div className="text-center">
          <div className="text-xl font-bold text-gray-900">{institution.adminCount}</div>
          <div className="text-xs text-gray-500">Admins</div>
        </div>
      </div>

      <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
        <div className="flex items-center space-x-1">
          <ClockIcon className="h-4 w-4" />
          <span>Last active: {institution.lastActivity}</span>
        </div>
        <div className="flex items-center space-x-1">
          <MapPinIcon className="h-4 w-4" />
          <span>{institution.slug}</span>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex space-x-2">
          <button
            onClick={() => handleViewInstitution(institution)}
            className="btn btn-ghost text-xs"
          >
            <EyeIcon className="h-4 w-4 mr-1" />
            View
          </button>
          <button
            onClick={() => handleEditInstitution(institution)}
            className="btn btn-ghost text-xs"
          >
            <PencilIcon className="h-4 w-4 mr-1" />
            Edit
          </button>
          <button
            onClick={() => handleDeleteInstitution(institution)}
            className="btn btn-ghost text-xs text-error-600 hover:bg-error-50"
          >
            <TrashIcon className="h-4 w-4 mr-1" />
            Delete
          </button>
        </div>
        <div className="flex items-center space-x-2 text-xs">
          {institution.settings.rfidEnabled && (
            <div className="w-2 h-2 bg-success-500 rounded-full" title="RFID Enabled" />
          )}
          {institution.settings.transportEnabled && (
            <div className="w-2 h-2 bg-primary-500 rounded-full" title="Transport Enabled" />
          )}
          {institution.settings.attendanceEnabled && (
            <div className="w-2 h-2 bg-warning-500 rounded-full" title="Attendance Enabled" />
          )}
        </div>
      </div>
    </motion.div>
  )

  // if (loading) {
  //   return (
  //     <div className="space-y-6">
  //       <div className="flex items-center justify-between">
  //         <div>
  //           <div className="h-8 bg-gray-200 rounded w-48 animate-pulse"></div>
  //           <div className="h-4 bg-gray-200 rounded w-96 mt-2 animate-pulse"></div>
  //         </div>
  //       </div>
  //       <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
  //         {[...Array(6)].map((_, i) => (
  //           <div key={i} className="dashboard-card animate-pulse">
  //             <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
  //             <div className="h-16 bg-gray-200 rounded mb-4"></div>
  //             <div className="h-4 bg-gray-200 rounded w-1/2"></div>
  //           </div>
  //         ))}
  //       </div>
  //     </div>
  //   )
  // }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Institutions Management</h1>
          <p className="text-gray-600 mt-1">
            Manage all educational institutions on the SmartTag platform
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn btn-primary"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Add Institution
        </button>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Institutions</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <BuildingOfficeIcon className="h-8 w-8 text-primary-600" />
          </div>
        </div>
        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Institutions</p>
              <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
            </div>
            <CheckCircleIcon className="h-8 w-8 text-success-600" />
          </div>
        </div>
        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Users</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalStudents}</p>
            </div>
            <UsersIcon className="h-8 w-8 text-indigo-600" />
          </div>
        </div>
        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Inactive Institutions</p>
              <p className="text-2xl font-bold text-gray-900">{stats.pendingApprovals}</p>
            </div>
            <ClockIcon className="h-8 w-8 text-warning-600" />
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
        <div className="flex-1 relative">
          <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search institutions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="form-input pl-10"
          />
        </div>

        <div className="flex items-center space-x-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="form-input text-sm"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="pending">Pending</option>
            <option value="inactive">Inactive</option>
            <option value="suspended">Suspended</option>
          </select>

          <select
            value={planFilter}
            onChange={(e) => setPlanFilter(e.target.value)}
            className="form-input text-sm"
          >
            <option value="all">All Plans</option>
            <option value="basic">Basic</option>
            <option value="premium">Premium</option>
            <option value="enterprise">Enterprise</option>
          </select>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="form-input text-sm"
          >
            <option value="all">All Types</option>
            <option value="primary">Primary</option>
            <option value="secondary">Secondary</option>
            <option value="university">University</option>
            <option value="kindergarten">Kindergarten</option>
          </select>

          <select
            value={`${sortBy}-${sortOrder}`}
            onChange={(e) => {
              const [field, order] = e.target.value.split('-');
              setSortBy(field);
              setSortOrder(order as 'asc' | 'desc');
            }}
            className="form-input text-sm"
          >
            <option value="createdAt-desc">Newest First</option>
            <option value="createdAt-asc">Oldest First</option>
            <option value="name-asc">Name A-Z</option>
            <option value="name-desc">Name Z-A</option>
            <option value="studentCount-desc">Most Students</option>
            <option value="teacherCount-desc">Most Teachers</option>
          </select>

          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded text-sm ${viewMode === 'grid' ? 'bg-white shadow-sm text-primary-600' : 'text-gray-600'}`}
            >
              Grid
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`p-2 rounded text-sm ${viewMode === 'table' ? 'bg-white shadow-sm text-primary-600' : 'text-gray-600'}`}
            >
              Table
            </button>
          </div>
        </div>
      </div>

      {/* Results Count */}
      <div className="flex items-center justify-between text-sm text-gray-600">
        <span>Showing {filteredInstitutions.length} of {institutions.length} institutions</span>
        <button
          onClick={() => toast.success('Export feature coming soon!')}
          className="btn btn-ghost text-sm"
        >
          Export Data
        </button>
      </div>

      {/* Institutions Grid */}
      {viewMode === 'grid' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence>
            {filteredInstitutions.map((institution) => (
              <InstitutionCard key={institution.id} institution={institution} />
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Institutions Table */}
      {viewMode === 'table' && (
        <div className="dashboard-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Institution
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Plan
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Students
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Activity
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredInstitutions.map((institution) => (
                  <tr key={institution.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-indigo-600 rounded-lg flex items-center justify-center mr-3">
                          <BuildingOfficeIcon className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{institution.name}</div>
                          <div className="text-sm text-gray-500">{institution.city}, {institution.state}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(institution.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getPlanBadge(institution.subscriptionPlan)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{institution.studentCount.toLocaleString()}</div>
                      <div className="text-sm text-gray-500">{institution.teacherCount} teachers</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {institution.lastActivity}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleViewInstitution(institution)}
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEditInstitution(institution)}
                          className="text-gray-600 hover:text-gray-900"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteInstitution(institution)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {filteredInstitutions.length === 0 && !loading && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="dashboard-card"
        >
          <div className="text-center py-12">
            <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No institutions found</h3>
            <p className="mt-2 text-gray-500">
              {searchQuery || statusFilter !== 'all' || planFilter !== 'all'
                ? 'Try adjusting your search or filters'
                : 'Get started by adding your first educational institution to the platform.'
              }
            </p>
            {(!searchQuery && statusFilter === 'all' && planFilter === 'all') && (
              <button
                onClick={() => setShowAddModal(true)}
                className="mt-4 btn btn-primary"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Add First Institution
              </button>
            )}
          </div>
        </motion.div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between bg-white px-6 py-4 border border-gray-200 rounded-lg">
          <div className="flex items-center">
            <p className="text-sm text-gray-700">
              Showing <span className="font-medium">{((currentPage - 1) * pageSize) + 1}</span> to{' '}
              <span className="font-medium">{Math.min(currentPage * pageSize, totalInstitutions)}</span> of{' '}
              <span className="font-medium">{totalInstitutions}</span> institutions
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              variant="secondary"
              size="sm"
            >
              <ArrowLeftIcon className="w-4 h-4 mr-1" />
              Previous
            </Button>

            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum = currentPage <= 3 ? i + 1 :
                               currentPage >= totalPages - 2 ? totalPages - 4 + i :
                               currentPage - 2 + i;

                if (pageNum < 1 || pageNum > totalPages) return null;

                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-1 rounded text-sm ${
                      currentPage === pageNum
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <Button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              variant="secondary"
              size="sm"
            >
              Next
              <ArrowRightIcon className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Two-Step Add Institution Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={handleModalClose}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">Add New Institution</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Step {currentStep} of 2: {currentStep === 1 ? 'Institution Details' : 'School Admin Details'}
                    </p>
                  </div>
                  <button
                    onClick={handleModalClose}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>

                {/* Progress Bar */}
                <div className="mt-4">
                  <div className="flex items-center">
                    <div className={`flex items-center ${currentStep >= 1 ? 'text-primary-600' : 'text-gray-300'}`}>
                      <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center text-sm font-medium ${currentStep >= 1 ? 'border-primary-600 bg-primary-600 text-white' : 'border-gray-300'}`}>
                        1
                      </div>
                      <span className="ml-2 text-sm font-medium">Institution</span>
                    </div>
                    <div className={`flex-1 h-1 mx-4 rounded-full ${currentStep >= 2 ? 'bg-primary-600' : 'bg-gray-200'}`} />
                    <div className={`flex items-center ${currentStep >= 2 ? 'text-primary-600' : 'text-gray-300'}`}>
                      <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center text-sm font-medium ${currentStep >= 2 ? 'border-primary-600 bg-primary-600 text-white' : 'border-gray-300'}`}>
                        2
                      </div>
                      <span className="ml-2 text-sm font-medium">Admin</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6">
                {currentStep === 1 ? (
                  /* Step 1: Institution Details */
                  <form onSubmit={institutionForm.handleSubmit(handleNextStep)} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <Input
                          {...institutionForm.register('name')}
                          label="Institution Name"
                          placeholder="e.g., Greenwood Academy"
                          error={institutionForm.formState.errors.name?.message}
                        />
                      </div>

                      {/* Logo Upload */}
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Institution Logo
                        </label>
                        <div className="flex items-start space-x-4">
                          {/* Logo Preview */}
                          <div className="flex-shrink-0">
                            <div className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
                              {logoPreview ? (
                                <img
                                  src={logoPreview}
                                  alt="Logo preview"
                                  className="w-full h-full object-cover rounded-lg"
                                />
                              ) : (
                                <PhotoIcon className="h-8 w-8 text-gray-400" />
                              )}
                            </div>
                          </div>

                          {/* Upload Controls */}
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <label className="btn btn-secondary cursor-pointer">
                                <CloudArrowUpIcon className="h-4 w-4 mr-2" />
                                {logoPreview ? 'Change Logo' : 'Upload Logo'}
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={handleLogoUpload}
                                  className="hidden"
                                />
                              </label>
                              {logoPreview && (
                                <button
                                  type="button"
                                  onClick={handleRemoveLogo}
                                  className="btn btn-ghost text-error-600 hover:bg-error-50"
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                            <p className="text-sm text-gray-500 mt-2">
                              Upload a square logo (PNG, JPG, or SVG). Maximum size: 5MB.
                            </p>
                            {logoFile && (
                              <p className="text-sm text-success-600 mt-1">
                                ✓ {logoFile.name} ({(logoFile.size / 1024 / 1024).toFixed(2)} MB)
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="md:col-span-2">
                        <Input
                          {...institutionForm.register('address')}
                          label="Physical Address"
                          placeholder="e.g., 123 Education Street"
                          error={institutionForm.formState.errors.address?.message}
                        />
                      </div>

                      <Input
                        {...institutionForm.register('city')}
                        label="City"
                        placeholder="e.g., Nairobi"
                        error={institutionForm.formState.errors.city?.message}
                      />

                      <Input
                        {...institutionForm.register('state')}
                        label="State/County"
                        placeholder="e.g., Nairobi County"
                        error={institutionForm.formState.errors.state?.message}
                      />

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Institution Phone Number *
                        </label>
                        <PhoneNumberInput
                          value={institutionForm.watch('phone') || ''}
                          countryCode={institutionForm.watch('countryCode') || 'KE'}
                          onChange={(phone, countryCode) => {
                            institutionForm.setValue('phone', phone)
                            institutionForm.setValue('countryCode', countryCode)
                            institutionForm.trigger(['phone', 'countryCode'])
                          }}
                          placeholder="Enter institution phone number"
                          error={institutionForm.formState.errors.phone?.message}
                          required
                        />
                      </div>

                      <Input
                        {...institutionForm.register('email')}
                        type="email"
                        label="Official Email"
                        placeholder="e.g., admin@greenwood.ac.ke"
                        error={institutionForm.formState.errors.email?.message}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Subscription Plan
                        </label>
                        <select
                          {...institutionForm.register('subscriptionPlan')}
                          className="form-input"
                        >
                          <option value="basic">Basic Plan</option>
                          <option value="premium">Premium Plan</option>
                          <option value="enterprise">Enterprise Plan</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Timezone
                        </label>
                        <select
                          {...institutionForm.register('timezone')}
                          className="form-input"
                        >
                          <option value="Africa/Nairobi">Africa/Nairobi (EAT)</option>
                          <option value="Africa/Lagos">Africa/Lagos (WAT)</option>
                          <option value="Africa/Cairo">Africa/Cairo (EET)</option>
                          <option value="UTC">UTC</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Currency
                        </label>
                        <select
                          {...institutionForm.register('currency')}
                          className="form-input"
                        >
                          <option value="KES">KES - Kenyan Shilling</option>
                          <option value="USD">USD - US Dollar</option>
                          <option value="EUR">EUR - Euro</option>
                          <option value="GBP">GBP - British Pound</option>
                        </select>
                      </div>
                    </div>

                    {/* Validation Methods Section */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Student Validation Methods *
                      </label>
                      <p className="text-sm text-gray-600 mb-4">
                        Select how students will validate their identity for attendance and access control.
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:border-primary-300 transition-colors">
                          <input
                            type="checkbox"
                            id="validation-bio"
                            {...institutionForm.register('validationMethods.bio')}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          />
                          <label htmlFor="validation-bio" className="flex-1 cursor-pointer">
                            <div className="text-sm font-medium text-gray-900">Biometric Authentication</div>
                            <div className="text-xs text-gray-500">Fingerprint or facial recognition</div>
                          </label>
                        </div>

                        <div className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:border-primary-300 transition-colors">
                          <input
                            type="checkbox"
                            id="validation-rfid-cards"
                            {...institutionForm.register('validationMethods.rfidCards')}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          />
                          <label htmlFor="validation-rfid-cards" className="flex-1 cursor-pointer">
                            <div className="text-sm font-medium text-gray-900">RFID Cards</div>
                            <div className="text-xs text-gray-500">Traditional access cards</div>
                          </label>
                        </div>

                        <div className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:border-primary-300 transition-colors">
                          <input
                            type="checkbox"
                            id="validation-wrist-bands"
                            {...institutionForm.register('validationMethods.wristBands')}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          />
                          <label htmlFor="validation-wrist-bands" className="flex-1 cursor-pointer">
                            <div className="text-sm font-medium text-gray-900">Smart Wrist Bands</div>
                            <div className="text-xs text-gray-500">Wearable RFID technology</div>
                          </label>
                        </div>

                        <div className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:border-primary-300 transition-colors">
                          <input
                            type="checkbox"
                            id="validation-key-chain"
                            {...institutionForm.register('validationMethods.keyChain')}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          />
                          <label htmlFor="validation-key-chain" className="flex-1 cursor-pointer">
                            <div className="text-sm font-medium text-gray-900">Key Chain Tags</div>
                            <div className="text-xs text-gray-500">Portable RFID key fobs</div>
                          </label>
                        </div>
                      </div>
                      {institutionForm.formState.errors.validationMethods && (
                        <p className="mt-2 text-sm text-red-600">
                          {institutionForm.formState.errors.validationMethods.message}
                        </p>
                      )}
                      <p className="text-sm text-gray-500 mt-2">
                        💡 You can enable additional validation methods later in the institution settings.
                      </p>
                    </div>

                    {/* Step 1 Actions */}
                    <div className="flex justify-between pt-6 border-t">
                      <Button
                        type="button"
                        onClick={handleModalClose}
                        variant="secondary"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        className="bg-primary-600 hover:bg-primary-700"
                      >
                        Next: Admin Details
                        <ArrowRightIcon className="h-4 w-4 ml-2" />
                      </Button>
                    </div>
                  </form>
                ) : (
                  /* Step 2: School Admin Details */
                  <form onSubmit={adminForm.handleSubmit(handleSubmitInstitution)} className="space-y-6">
                    <div className="text-center mb-6">
                      <div className="w-16 h-16 bg-gradient-to-br from-primary-600 to-indigo-700 rounded-full mx-auto flex items-center justify-center mb-4">
                        <ShieldCheckIcon className="h-8 w-8 text-white" />
                      </div>
                      <h4 className="text-lg font-semibold text-gray-900">School Administrator Account</h4>
                      <p className="text-sm text-gray-600">
                        Create the primary administrator account for {institutionFormData?.name}
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input
                        {...adminForm.register('firstName')}
                        label="First Name"
                        placeholder="e.g., John"
                        error={adminForm.formState.errors.firstName?.message}
                      />

                      <Input
                        {...adminForm.register('lastName')}
                        label="Last Name"
                        placeholder="e.g., Smith"
                        error={adminForm.formState.errors.lastName?.message}
                      />

                      <Input
                        {...adminForm.register('email')}
                        type="email"
                        label="Admin Email"
                        placeholder="e.g., admin@greenwood.ac.ke"
                        error={adminForm.formState.errors.email?.message}
                      />

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Phone Number *
                        </label>
                        <PhoneNumberInput
                          value={adminForm.watch('phone') || ''}
                          countryCode={adminForm.watch('countryCode') || 'KE'}
                          onChange={(phone, countryCode) => {
                            adminForm.setValue('phone', phone)
                            adminForm.setValue('countryCode', countryCode)
                            adminForm.trigger(['phone', 'countryCode'])
                          }}
                          placeholder="Enter phone number"
                          error={adminForm.formState.errors.phone?.message}
                          required
                        />
                      </div>
                    </div>

                    {/* School Admin Role Group Selection */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        School Admin Role Group *
                      </label>
                      {loadingGroups ? (
                        <div className="form-input flex items-center justify-center text-gray-500 py-2">
                          Loading School Admin groups...
                        </div>
                      ) : (
                        <select
                          {...adminForm.register('roleId')}
                          className="form-input"
                        >
                          <option value="">Select School Admin Group</option>
                          {availableGroups.map(group => (
                            <option key={group.id} value={group.id}>
                              {group.name}
                            </option>
                          ))}
                        </select>
                      )}
                      {adminForm.formState.errors.roleId && (
                        <p className="mt-1 text-sm text-red-600">{adminForm.formState.errors.roleId.message}</p>
                      )}
                      <p className="text-sm text-gray-500 mt-1">
                        Required: Assign the admin to a School Admin role group
                      </p>
                      {availableGroups.length === 0 && !loadingGroups && (
                        <p className="text-sm text-orange-600 mt-1">
                          ⚠️ No School Admin groups available. Create one first in Groups management.
                        </p>
                      )}
                    </div>

                    {/* System Defaults Notice */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <CheckCircleIcon className="h-5 w-5 text-blue-600 mt-0.5" />
                        <div>
                          <h5 className="text-sm font-medium text-blue-900">Default Settings</h5>
                          <div className="text-sm text-blue-700 mt-1 space-y-1">
                            <p>• A secure password will be automatically generated</p>
                            <p>• Welcome email with login credentials will be sent</p>
                            <p>• Admin account will be activated immediately</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Summary Section */}
                    {institutionFormData && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h5 className="font-medium text-gray-900 mb-3">Institution Summary</h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="text-gray-600">Name: </span>
                            <span className="font-medium text-gray-900">{institutionFormData.name}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Location: </span>
                            <span className="font-medium text-gray-900">{institutionFormData.city}, {institutionFormData.state}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Plan: </span>
                            <span className="font-medium text-gray-900">{institutionFormData.subscriptionPlan}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Contact: </span>
                            <span className="font-medium text-gray-900">{institutionFormData.email}</span>
                          </div>
                          <div className="md:col-span-2">
                            <span className="text-gray-600">Validation Methods: </span>
                            <div className="mt-1 flex flex-wrap gap-2">
                              {institutionFormData.validationMethods.bio && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  Biometric
                                </span>
                              )}
                              {institutionFormData.validationMethods.rfidCards && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  RFID Cards
                                </span>
                              )}
                              {institutionFormData.validationMethods.wristBands && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                  Wrist Bands
                                </span>
                              )}
                              {institutionFormData.validationMethods.keyChain && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                  Key Chain
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Step 2 Actions */}
                    <div className="flex justify-between pt-6 border-t">
                      <Button
                        type="button"
                        onClick={handlePrevStep}
                        variant="secondary"
                      >
                        <ArrowLeftIcon className="h-4 w-4 mr-2" />
                        Back
                      </Button>
                      <Button
                        type="submit"
                        loading={isSubmitting}
                        disabled={isSubmitting}
                        className="bg-success-600 hover:bg-success-700"
                      >
                        {isSubmitting ? 'Creating Institution...' : 'Create Institution'}
                        <CheckCircleIcon className="h-4 w-4 ml-2" />
                      </Button>
                    </div>
                  </form>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Institution Details Modal */}
      <AnimatePresence>
        {selectedInstitution && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => setSelectedInstitution(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">{selectedInstitution.name}</h3>
                  <p className="text-gray-600">{selectedInstitution.city}, {selectedInstitution.state}</p>
                </div>
                <button
                  onClick={() => setSelectedInstitution(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Basic Information */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">Basic Information</h4>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <MapPinIcon className="h-4 w-4 text-gray-400" />
                      <span className="text-sm">{selectedInstitution.address}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <PhoneIcon className="h-4 w-4 text-gray-400" />
                      <span className="text-sm">{selectedInstitution.phone}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <EnvelopeIcon className="h-4 w-4 text-gray-400" />
                      <span className="text-sm">{selectedInstitution.email}</span>
                    </div>
                  </div>
                </div>

                {/* Statistics */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">Statistics</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <div className="text-lg font-bold text-gray-900">{selectedInstitution.studentCount.toLocaleString()}</div>
                      <div className="text-xs text-gray-500">Students</div>
                    </div>
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <div className="text-lg font-bold text-gray-900">{selectedInstitution.teacherCount}</div>
                      <div className="text-xs text-gray-500">Teachers</div>
                    </div>
                  </div>
                </div>

                {/* Settings */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">Platform Features</h4>
                  <div className="space-y-2">
                    {Object.entries({
                      'RFID Tracking': selectedInstitution.settings.rfidEnabled,
                      'Transport Management': selectedInstitution.settings.transportEnabled,
                      'Attendance System': selectedInstitution.settings.attendanceEnabled,
                      'Meal Services': selectedInstitution.settings.mealsEnabled,
                    }).map(([feature, enabled]) => (
                      <div key={feature} className="flex items-center justify-between">
                        <span className="text-sm text-gray-700">{feature}</span>
                        <span className={`text-xs px-2 py-1 rounded-full ${enabled ? 'bg-success-100 text-success-700' : 'bg-gray-100 text-gray-500'}`}>
                          {enabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Validation Methods */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">Student Validation Methods</h4>
                  <div className="space-y-3">
                    {selectedInstitution.validationMethods ? (
                      <div className="grid grid-cols-2 gap-3">
                        <div className={`p-3 rounded-lg border-2 ${selectedInstitution.validationMethods.bio ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-gray-50'}`}>
                          <div className="flex items-center space-x-2">
                            <div className={`w-3 h-3 rounded-full ${selectedInstitution.validationMethods.bio ? 'bg-blue-500' : 'bg-gray-400'}`} />
                            <div>
                              <div className="text-sm font-medium text-gray-900">Biometric</div>
                              <div className="text-xs text-gray-500">Fingerprint/Face</div>
                            </div>
                          </div>
                        </div>
                        
                        <div className={`p-3 rounded-lg border-2 ${selectedInstitution.validationMethods.rfidCards ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
                          <div className="flex items-center space-x-2">
                            <div className={`w-3 h-3 rounded-full ${selectedInstitution.validationMethods.rfidCards ? 'bg-green-500' : 'bg-gray-400'}`} />
                            <div>
                              <div className="text-sm font-medium text-gray-900">RFID Cards</div>
                              <div className="text-xs text-gray-500">Access Cards</div>
                            </div>
                          </div>
                        </div>
                        
                        <div className={`p-3 rounded-lg border-2 ${selectedInstitution.validationMethods.wristBands ? 'border-purple-200 bg-purple-50' : 'border-gray-200 bg-gray-50'}`}>
                          <div className="flex items-center space-x-2">
                            <div className={`w-3 h-3 rounded-full ${selectedInstitution.validationMethods.wristBands ? 'bg-purple-500' : 'bg-gray-400'}`} />
                            <div>
                              <div className="text-sm font-medium text-gray-900">Wrist Bands</div>
                              <div className="text-xs text-gray-500">Wearable RFID</div>
                            </div>
                          </div>
                        </div>
                        
                        <div className={`p-3 rounded-lg border-2 ${selectedInstitution.validationMethods.keyChain ? 'border-orange-200 bg-orange-50' : 'border-gray-200 bg-gray-50'}`}>
                          <div className="flex items-center space-x-2">
                            <div className={`w-3 h-3 rounded-full ${selectedInstitution.validationMethods.keyChain ? 'bg-orange-500' : 'bg-gray-400'}`} />
                            <div>
                              <div className="text-sm font-medium text-gray-900">Key Chain</div>
                              <div className="text-xs text-gray-500">Portable Tags</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 italic">No validation methods configured</p>
                    )}
                    
                    {selectedInstitution.validationMethods && (
                      <div className="pt-2 border-t border-gray-200">
                        <div className="flex flex-wrap gap-2">
                          {selectedInstitution.validationMethods.bio && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              Biometric Enabled
                            </span>
                          )}
                          {selectedInstitution.validationMethods.rfidCards && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              RFID Cards Enabled
                            </span>
                          )}
                          {selectedInstitution.validationMethods.wristBands && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              Wrist Bands Enabled
                            </span>
                          )}
                          {selectedInstitution.validationMethods.keyChain && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                              Key Chain Enabled
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Subscription */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">Subscription Details</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">Plan</span>
                      {getPlanBadge(selectedInstitution.subscriptionPlan)}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">Status</span>
                      {getStatusBadge(selectedInstitution.status)}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">Member Since</span>
                      <span className="text-sm text-gray-900">{selectedInstitution.createdAt}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6 pt-6 border-t">
                <button
                  onClick={() => toast.success('Edit functionality coming soon!')}
                  className="btn btn-secondary"
                >
                  <PencilIcon className="h-4 w-4 mr-2" />
                  Edit Institution
                </button>
                <button
                  onClick={() => toast.success('Analytics coming soon!')}
                  className="btn btn-primary"
                >
                  <ChartBarIcon className="h-4 w-4 mr-2" />
                  View Analytics
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default InstitutionsPage
